import { useCallback, useMemo, useRef, useState } from 'react'
import {
  executeScratch,
  getScratchToken,
  type ScratchOutcomeResult,
} from '@/lib/api/scratch-campaigns-public.service'
import { ApiClientError } from '@/lib/api/client'
import type { RaspadinhaPublicCampaign } from '@/types/raspadinha-public-campaign'
import { RaspadinhaPublicContemplatedPrizeStep } from './RaspadinhaPublicContemplatedPrizeStep'
import { RaspadinhaScratchCard } from './RaspadinhaScratchCard'
import styles from './RaspadinhaPublicScratchStep.module.css'

type RaspadinhaPublicScratchStepProps = {
  campaign: RaspadinhaPublicCampaign
}

type ScratchExperiencePhase = 'scratch' | 'prize'

function hasImageUrl(url: string): boolean {
  return url.trim().length > 0
}

export function RaspadinhaPublicScratchStep({ campaign }: RaspadinhaPublicScratchStepProps) {
  const missingScratchImageError = !hasImageUrl(campaign.scratch_image_url)
    ? 'A imagem a ser raspada não está configurada nesta campanha.'
    : null

  const [phase, setPhase] = useState<ScratchExperiencePhase>('scratch')
  const [outcomeBundle, setOutcomeBundle] = useState<ScratchOutcomeResult | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const resolveOutcomePromiseRef = useRef<Promise<ScratchOutcomeResult> | null>(null)

  const resolveOutcome = useCallback(async (): Promise<ScratchOutcomeResult> => {
    if (outcomeBundle) {
      return outcomeBundle
    }

    if (resolveOutcomePromiseRef.current) {
      return resolveOutcomePromiseRef.current
    }

    const token = getScratchToken(campaign.slug)
    if (!token) {
      const message = 'Sessão de raspadinha expirada. Volte e faça o cadastro novamente.'
      setLoadError(message)
      throw new ApiClientError(message, 401)
    }

    const pending = executeScratch(campaign.slug)
      .then((result) => {
        setOutcomeBundle(result)
        setLoadError(null)
        return result
      })
      .catch((error) => {
        resolveOutcomePromiseRef.current = null
        const message =
          error instanceof ApiClientError
            ? error.message
            : 'Não foi possível registrar sua raspadinha.'
        setLoadError(message)
        throw error
      })

    resolveOutcomePromiseRef.current = pending
    return pending
  }, [campaign.slug, outcomeBundle])

  const contemplatedPrizeImageUrl = outcomeBundle?.contemplated_prize?.image_url ?? null
  const canOpenContemplatedPrize =
    outcomeBundle?.outcome === 'contemplated' && contemplatedPrizeImageUrl !== null

  const prizeTitle = useMemo(() => {
    return outcomeBundle?.contemplated_prize?.title.trim() || 'Prêmio contemplado'
  }, [outcomeBundle])

  const resultImageUrl =
    outcomeBundle?.result_image_url.trim() || campaign.scratch_image_url

  if (missingScratchImageError || loadError) {
    return (
      <section className={styles.step} aria-live="polite">
        <p className={styles.errorMessage} role="alert">
          {missingScratchImageError ?? loadError}
        </p>
      </section>
    )
  }

  if (phase === 'prize' && contemplatedPrizeImageUrl) {
    return (
      <RaspadinhaPublicContemplatedPrizeStep
        slug={campaign.slug}
        imageUrl={contemplatedPrizeImageUrl}
        prizeTitle={prizeTitle}
      />
    )
  }

  return (
    <section className={styles.step} aria-label="Raspadinha da campanha">
      <RaspadinhaScratchCard
        scratchImageUrl={campaign.scratch_image_url}
        resultImageUrl={resultImageUrl}
        canInteractAfterReveal={canOpenContemplatedPrize}
        onBeforeFirstScratch={resolveOutcome}
        onRevealedInteract={() => setPhase('prize')}
      />
    </section>
  )
}
